import { APP_CONFIG } from '@/lib/constants';
import { API_ENDPOINTS } from './endpoints';
import {
  DashboardResponse,
  StatusResponse,
  PositionsResponse,
  PnlResponse,
  TodayResponse,
  WeekResponse,
  TradesResponse,
  BuyHistoryResponse,
  SellHistoryResponse,
  RiskResponse,
  HealthResponse,
  SignalsResponse,
  OrdersResponse,
  LoginRequest,
  LoginResponse,
  AuthMeResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  SettingsResponse,
  SettingsPatchRequest,
  SettingsPatchResponse,
  ResetCircuitBreakerRequest,
  ResetCircuitBreakerResponse,
  ApiError,
} from '@/types/api';
import {
  Position,
  Trade,
  Order,
  OrderStatus,
  Signal,
  SignalStatus,
} from '@/types/trading';
import {
  mockDashboardData,
  mockStatusData,
  mockPositionsData,
  mockPnlData,
  mockTodayData,
  mockWeekData,
  mockTradesData,
  mockBuyHistoryData,
  mockSellHistoryData,
  mockRiskData,
  mockHealthData,
  mockSignalsData,
  mockOrdersData,
  mockAuthMeData,
  mockSettingsData,
  mockResetCircuitBreakerData,
} from './mockData';

// Normalization Helpers to bridge raw backend JSON with strongly-typed UI models

function normalizeTrade(t: Record<string, unknown>): Trade {
  const sideStr = String(t.side || t.transaction_type || 'BUY').toUpperCase();
  const isBuy = sideStr === 'BUY';
  const pnlVal = !isBuy && t.pnl !== undefined && t.pnl !== null
    ? Number(t.pnl)
    : (!isBuy && t.realized_pnl !== undefined && t.realized_pnl !== null ? Number(t.realized_pnl) : null);
  const pnlPct = !isBuy && t.pnl_percent !== undefined && t.pnl_percent !== null ? Number(t.pnl_percent) : null;
  const rawStatus = String(t.status || '').toUpperCase().trim();
  const orderTypeStr = String(t.order_type || 'MARKET').toUpperCase();

  let mappedStatus: OrderStatus = 'FILLED';
  if (rawStatus === 'COMPLETE' || rawStatus === 'FILLED' || rawStatus === 'EXECUTED' || rawStatus === 'COMPLETED') {
    mappedStatus = 'FILLED';
  } else if (rawStatus === 'REJECTED' || rawStatus === 'FAILED' || rawStatus === 'SUBMIT_FAILED') {
    mappedStatus = 'REJECTED';
  } else if (rawStatus === 'PENDING' || rawStatus === 'SUBMITTED' || rawStatus === 'OPEN' || rawStatus === 'QUEUED') {
    mappedStatus = 'PENDING';
  } else if (rawStatus === 'CANCELLED' || rawStatus === 'CANCELED') {
    mappedStatus = 'CANCELLED';
  } else if (rawStatus === 'EXPIRED') {
    mappedStatus = 'EXPIRED';
  } else if (rawStatus === '') {
    // Invariant: Backend paper_journal entries represent executed trade fills and omit an explicit status column
    mappedStatus = 'FILLED';
  } else {
    mappedStatus = 'UNKNOWN';
  }

  return {
    id: String(t.id ?? `${t.symbol || 'trade'}-${t.timestamp || Date.now()}`),
    symbol: String(t.symbol || 'UNKNOWN'),
    side: (sideStr === 'SELL' ? 'SELL' : 'BUY'),
    quantity: Number(t.quantity || 0),
    price: Number(t.price || 0),
    order_type: orderTypeStr,
    pnl: pnlVal,
    pnl_percent: pnlPct,
    status: mappedStatus,
    timestamp: String(t.timestamp || t.executed_at || new Date().toISOString()),
    reason: t.reason ? String(t.reason) : (t.exit_reason ? String(t.exit_reason) : null),
  };
}

function normalizeSignal(s: Record<string, unknown>): Signal {
  const rawType = s.signal_type || s.action;
  const signalType = rawType ? String(rawType).toUpperCase() : null;
  const statusStr = String(s.status || s.validation_status || '').toUpperCase().trim();
  const status = (statusStr || 'RECEIVED') as SignalStatus;
  const symbol = String(s.symbol || s.parsed_symbol || 'UNKNOWN');
  const rawId = typeof s.id === 'number' ? s.id : (s.id && !isNaN(Number(s.id)) ? Number(s.id) : null);

  const receivedAt = s.received_at ? String(s.received_at) : null;
  const signalTimestamp = s.signal_timestamp ? String(s.signal_timestamp) : null;
  const riskDecisionAt = s.risk_decision_at ? String(s.risk_decision_at) : null;
  const updatedAt = s.updated_at ? String(s.updated_at) : null;
  const createdAt = s.created_at ? String(s.created_at) : null;
  const timestamp = signalTimestamp || receivedAt || createdAt || (s.timestamp ? String(s.timestamp) : null);

  const priceVal = s.price !== undefined && s.price !== null && !isNaN(Number(s.price)) ? Number(s.price) : null;
  const orderAttempted = s.order_attempted !== undefined && s.order_attempted !== null ? Boolean(s.order_attempted) : null;

  return {
    id: String(s.id ?? `${symbol || 'sig'}-${receivedAt || createdAt || 'unknown'}`),
    raw_id: rawId,
    source_chat_id: s.source_chat_id ? String(s.source_chat_id) : null,
    source_message_id: s.source_message_id ? String(s.source_message_id) : null,
    received_at: receivedAt,
    signal_timestamp: signalTimestamp,
    timestamp,
    symbol,
    parsed_symbol: symbol,
    signal_type: signalType,
    price: priceVal,
    status,
    validation_status: s.validation_status ? String(s.validation_status) : status,
    execution_state: String(s.execution_state || s.execution_status || status || 'SIGNAL_RECEIVED'),
    rejection_reason: s.rejection_reason ? String(s.rejection_reason) : null,
    outcome_reason_code: s.outcome_reason_code ? String(s.outcome_reason_code) : null,
    outcome_reason: s.outcome_reason ? String(s.outcome_reason) : (s.rejection_reason ? String(s.rejection_reason) : null),
    risk_decision_at: riskDecisionAt,
    order_attempted: orderAttempted,
    order_id: s.order_id ? String(s.order_id) : null,
    position_id: s.position_id ? String(s.position_id) : null,
    execution_status: s.execution_status ? String(s.execution_status) : null,
    updated_at: updatedAt,
    created_at: createdAt,
    raw_message: s.raw_message ? String(s.raw_message) : null,
  };
}

function normalizeOrder(o: Record<string, unknown>): Order {
  const sideStr = String(o.side || o.transaction_type || 'BUY').toUpperCase();
  const typeStr = String(o.order_type || 'MARKET').toUpperCase();
  const rawStatus = String(o.status || '').toUpperCase().trim();
  const orderPrice = Number(o.price ?? o.fill_price ?? o.limit_price ?? o.requested_price ?? 0);
  const signalId = o.signal_id !== undefined && o.signal_id !== null ? (typeof o.signal_id === 'number' ? o.signal_id : String(o.signal_id)) : null;

  let mappedStatus: OrderStatus = 'UNKNOWN';
  if (rawStatus === 'COMPLETE' || rawStatus === 'FILLED' || rawStatus === 'EXECUTED') {
    mappedStatus = 'FILLED';
  } else if (rawStatus === 'SUBMITTED' || rawStatus === 'PENDING' || rawStatus === 'OPEN' || rawStatus === 'QUEUED') {
    mappedStatus = 'PENDING';
  } else if (rawStatus === 'REJECTED' || rawStatus === 'FAILED' || rawStatus === 'SUBMIT_FAILED') {
    mappedStatus = 'REJECTED';
  } else if (rawStatus === 'CANCELLED' || rawStatus === 'CANCELED') {
    mappedStatus = 'CANCELLED';
  } else if (rawStatus === 'EXPIRED') {
    mappedStatus = 'EXPIRED';
  } else {
    mappedStatus = 'UNKNOWN';
  }

  return {
    id: String(o.id ?? `${o.symbol || 'ord'}-${o.timestamp || o.requested_at || 'unknown'}`),
    order_id: String(o.order_id || o.broker_order_id || (o.id ? `ORD-${o.id}` : 'ORD-UNKNOWN')),
    symbol: String(o.symbol || 'UNKNOWN'),
    side: (sideStr === 'SELL' ? 'SELL' : 'BUY'),
    quantity: Number(o.quantity || o.requested_qty || 0),
    price: orderPrice,
    order_type: (typeStr === 'LIMIT' ? 'LIMIT' : 'MARKET'),
    status: mappedStatus,
    timestamp: String(o.timestamp || o.requested_at || o.created_at || ''),
    reason: o.reason ? String(o.reason) : null,
    rejection_reason: o.rejection_reason ? String(o.rejection_reason) : null,
    signal_id: signalId,
  };
}

function normalizePosition(p: Record<string, unknown>): Position {
  const entryPrice = Number(p.entry_price || p.price || 0);
  const rawLtp = p.current_ltp !== undefined && p.current_ltp !== null
    ? p.current_ltp
    : (p.ltp !== undefined && p.ltp !== null ? p.ltp : (p.current_price !== undefined && p.current_price !== null ? p.current_price : null));
  const ltp = rawLtp !== null && !isNaN(Number(rawLtp)) ? Number(rawLtp) : null;
  const qty = Number(p.quantity || 0);

  let unrealizedPnl = p.unrealized_pnl !== undefined && p.unrealized_pnl !== null ? Number(p.unrealized_pnl) : null;
  let unrealizedPnlPct = p.unrealized_pnl_percent !== undefined && p.unrealized_pnl_percent !== null
    ? Number(p.unrealized_pnl_percent)
    : (p.pnl_percent !== undefined && p.pnl_percent !== null ? Number(p.pnl_percent) : null);

  if (unrealizedPnl === null && ltp !== null && entryPrice > 0) {
    unrealizedPnl = (ltp - entryPrice) * qty;
    unrealizedPnlPct = ((ltp - entryPrice) / entryPrice) * 100;
  }

  const rawSl = p.sl_price !== undefined && p.sl_price !== null
    ? p.sl_price
    : (p.stop_loss !== undefined && p.stop_loss !== null ? p.stop_loss : null);
  const stopLoss = rawSl !== null && !isNaN(Number(rawSl)) ? Number(rawSl) : null;

  const rawTp = p.tp_price !== undefined && p.tp_price !== null
    ? p.tp_price
    : (p.take_profit !== undefined && p.take_profit !== null ? p.take_profit : null);
  const takeProfit = rawTp !== null && !isNaN(Number(rawTp)) ? Number(rawTp) : null;

  let holdingDuration: number | string | null = null;
  if (p.holding_minutes !== undefined && p.holding_minutes !== null) {
    holdingDuration = typeof p.holding_minutes === 'number' ? p.holding_minutes : (isNaN(Number(p.holding_minutes)) ? String(p.holding_minutes) : Number(p.holding_minutes));
  } else if (p.holding_duration !== undefined && p.holding_duration !== null) {
    holdingDuration = typeof p.holding_duration === 'number' ? p.holding_duration : (isNaN(Number(p.holding_duration)) ? String(p.holding_duration) : Number(p.holding_duration));
  } else if (p.opened_at) {
    holdingDuration = String(p.opened_at);
  }

  const statusStr = String(p.status || 'OPEN').toUpperCase();

  return {
    id: String(p.id || p.symbol || Math.random().toString(36).substring(2)),
    symbol: String(p.symbol || 'UNKNOWN'),
    exchange: String(p.exchange || 'NSE'),
    quantity: qty,
    entry_price: entryPrice,
    ltp,
    unrealized_pnl: unrealizedPnl,
    unrealized_pnl_percent: unrealizedPnlPct,
    stop_loss: stopLoss,
    take_profit: takeProfit,
    holding_duration: holdingDuration,
    opened_at: String(p.opened_at || p.timestamp || new Date().toISOString()),
    status: (statusStr === 'CLOSED' ? 'CLOSED' : 'OPEN'),
  };
}

class ApiClient {
  private baseUrl: string;
  private timeoutMs: number;
  private forceMock: boolean;
  private onUnauthorizedCallback: (() => void) | null = null;

  constructor(baseUrl = APP_CONFIG.apiBaseUrl, timeoutMs = 8000) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.timeoutMs = timeoutMs;
    this.forceMock = false;
  }

  public setBaseUrl(url: string) {
    this.baseUrl = url.replace(/\/$/, '');
  }

  public setForceMock(enabled: boolean) {
    this.forceMock = enabled;
  }

  public isMockEnabled(): boolean {
    return this.forceMock;
  }

  public setOnUnauthorized(callback: () => void) {
    this.onUnauthorizedCallback = callback;
  }

  /**
   * Unified HTTP request handler enforcing credentials: 'include', CSRF headers on mutations,
   * and structured ApiError mapping.
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body?: unknown,
    mockFallback?: T
  ): Promise<T> {
    if (this.forceMock && mockFallback !== undefined) {
      await new Promise((resolve) => setTimeout(resolve, 80));
      return mockFallback;
    }

    const isAuthOrSettings =
      path === API_ENDPOINTS.AUTH_ME ||
      path === API_ENDPOINTS.AUTH_LOGIN ||
      path === API_ENDPOINTS.AUTH_LOGOUT ||
      path === API_ENDPOINTS.AUTH_CHANGE_PASSWORD ||
      path === API_ENDPOINTS.SETTINGS ||
      path === API_ENDPOINTS.RISK_RESET_CIRCUIT_BREAKER;

    const isPublicAuthCheck = path === API_ENDPOINTS.AUTH_LOGIN || path === API_ENDPOINTS.AUTH_ME;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    if (method === 'POST' || method === 'PATCH') {
      headers['Content-Type'] = 'application/json';
      headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    try {
      const url = `${this.baseUrl}${path}`;
      const response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorDetail = '';
        let errorData: unknown = null;
        try {
          const json = await response.json();
          errorData = json;
          if (json && typeof json.detail === 'string') {
            errorDetail = json.detail;
          } else if (json && typeof json.detail === 'object') {
            errorDetail = JSON.stringify(json.detail);
          } else if (json && typeof json.message === 'string') {
            errorDetail = json.message;
          }
        } catch {
          // Response body was not JSON
        }

        const retryAfterHeader = response.headers.get('Retry-After');
        const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;

        // Trigger centralized session expiry callback only for genuinely protected queries
        if (response.status === 401 && !isPublicAuthCheck && this.onUnauthorizedCallback) {
          this.onUnauthorizedCallback();
        }

        const error: ApiError = {
          name: 'ApiError',
          message: errorDetail || `HTTP ${response.status}: ${response.statusText || 'API Error'}`,
          status: response.status,
          status_code: response.status,
          details: errorData,
          retry_after: !isNaN(Number(retryAfter)) ? retryAfter : undefined,
        };
        throw error;
      }

      const data = await response.json();
      return data as T;
    } catch (err: unknown) {
      clearTimeout(timeoutId);

      // If it's already an ApiError with status / status_code, rethrow it
      if (typeof err === 'object' && err !== null && ('status_code' in err || 'status' in err)) {
        throw err;
      }

      // If explicit testing mock mode is active
      if (this.forceMock && mockFallback !== undefined) {
        return mockFallback;
      }

      // Production rule: Auth and Settings must never fall back to mock data on network error
      if (!isAuthOrSettings && APP_CONFIG.enableMockFallback && mockFallback !== undefined) {
        return mockFallback;
      }

      const error: ApiError = {
        name: 'ApiError',
        message: err instanceof Error ? err.message : 'Network request failed',
      };
      throw error;
    }
  }

  private async get<T>(path: string, mockFallback: T): Promise<Record<string, unknown>> {
    return this.request('GET', path, undefined, mockFallback) as Promise<Record<string, unknown>>;
  }

  private async post<T>(path: string, body?: unknown, mockFallback?: T): Promise<T> {
    return this.request('POST', path, body, mockFallback);
  }

  private async patch<T>(path: string, body?: unknown, mockFallback?: T): Promise<T> {
    return this.request('PATCH', path, body, mockFallback);
  }

  // Authoritative Endpoint Normalization Methods
  public async getDashboard(): Promise<DashboardResponse> {
    const raw = await this.get(API_ENDPOINTS.DASHBOARD, mockDashboardData);
    if (raw.bot_status && raw.pnl_summary) {
      return raw as unknown as DashboardResponse;
    }

    const statusObj = (raw.status || {}) as Record<string, unknown>;
    const pnlObj = (raw.pnl || {}) as Record<string, unknown>;
    const positionsObj = (raw.positions || {}) as Record<string, unknown>;
    const riskObj = (raw.risk || {}) as Record<string, unknown>;
    const healthObj = (raw.health || {}) as Record<string, unknown>;
    const subsystems = (healthObj.subsystems || {}) as Record<string, unknown>;

    const maxPos = Number(positionsObj.max_positions ?? 2);
    const posItems = Array.isArray(positionsObj.items) ? positionsObj.items : [];
    const activeCount = Number(positionsObj.active_count ?? posItems.length);

    const rawRecentTrades = Array.isArray(raw.recent_trades) ? raw.recent_trades : [];
    const rawRecentSignals = Array.isArray(raw.recent_signals) ? raw.recent_signals : [];

    const cbHalted = Boolean(pnlObj.circuit_breaker_halted ?? riskObj.circuit_breaker_halted ?? false);

    return {
      bot_status: {
        status: statusObj.trading_enabled ? 'RUNNING' : String(statusObj.mode || 'RUNNING').toUpperCase(),
        mode: String(statusObj.mode || 'PAPER').toUpperCase(),
        market_status: String(statusObj.market_state || 'CLOSED').toUpperCase(),
        uptime: String(statusObj.uptime_str || (statusObj.uptime_seconds ? `${Math.floor(Number(statusObj.uptime_seconds) / 3600)}h` : '—')),
        last_cycle: subsystems.last_cycle ? String(subsystems.last_cycle) : null,
        last_signal: subsystems.last_received_signal ? String(subsystems.last_received_signal) : null,
        broker_connected: Boolean(statusObj.trading_enabled ?? true),
      },
      pnl_summary: {
        today_pnl: Number(pnlObj.today_realized_pnl ?? pnlObj.today_pnl ?? pnlObj.total_realized_pnl ?? 0),
        realized_pnl: Number(pnlObj.today_realized_pnl ?? pnlObj.realized_pnl ?? pnlObj.total_realized_pnl ?? 0),
        unrealized_pnl: pnlObj.unrealized_pnl !== undefined && pnlObj.unrealized_pnl !== null ? Number(pnlObj.unrealized_pnl) : null,
        total_pnl: Number(pnlObj.total_pnl ?? pnlObj.today_realized_pnl ?? pnlObj.total_realized_pnl ?? 0),
        weekly_pnl: Number(pnlObj.weekly_pnl ?? 0),
        win_rate: Number(pnlObj.win_rate ?? 0),
        total_trades: Number(pnlObj.trade_count ?? pnlObj.total_trades ?? 0),
        winning_trades: Number(pnlObj.winning_trades ?? 0),
        losing_trades: Number(pnlObj.losing_trades ?? 0),
        circuit_breaker_halted: cbHalted,
      },
      positions_summary: {
        open_positions_count: activeCount,
        max_positions: maxPos,
        available_capacity: Math.max(0, maxPos - activeCount),
      },
      risk_summary: {
        trading_allowed: Boolean(riskObj.trading_allowed ?? true),
        permission_reason: riskObj.permission_reason ? String(riskObj.permission_reason) : null,
        daily_loss: Number(riskObj.daily_loss_used ?? riskObj.daily_loss ?? 0),
        daily_loss_limit: Number(riskObj.max_daily_loss ?? riskObj.daily_loss_limit ?? 2000),
        trades_today: Number(riskObj.daily_entries_used ?? riskObj.trades_today ?? 0),
        max_trades_per_day: Number(riskObj.max_daily_entries ?? riskObj.max_trades_per_day ?? 10),
        consecutive_losses: Number(riskObj.consecutive_losses ?? 0),
        max_consecutive_losses: Number(riskObj.max_consecutive_losses ?? 3),
        kill_switch_active: Boolean(riskObj.kill_switch_active ?? false),
        circuit_breaker_triggered: Boolean(cbHalted || riskObj.circuit_breaker_triggered || riskObj.kill_switch_active || false),
        circuit_breaker_halted: cbHalted,
      },
      recent_trades: rawRecentTrades.map((t) => normalizeTrade(t as Record<string, unknown>)),
      recent_signals: rawRecentSignals.map((s) => normalizeSignal(s as Record<string, unknown>)),
    };
  }

  public async getStatus(): Promise<StatusResponse> {
    const raw = await this.get(API_ENDPOINTS.STATUS, mockStatusData);
    return {
      status: raw.trading_enabled ? 'RUNNING' : String(raw.status || 'RUNNING'),
      mode: String(raw.mode || 'PAPER').toUpperCase(),
      market_status: String(raw.market_state || raw.market_status || 'CLOSED').toUpperCase(),
      uptime: String(raw.uptime_str || (raw.uptime_seconds ? `${Math.floor(Number(raw.uptime_seconds) / 3600)}h` : '—')),
      uptime_seconds: raw.uptime_seconds !== undefined ? Number(raw.uptime_seconds) : undefined,
      last_cycle: raw.last_cycle ? String(raw.last_cycle) : null,
      last_signal: raw.last_signal ? String(raw.last_signal) : null,
      broker_connected: Boolean(raw.trading_enabled ?? raw.broker_connected ?? true),
      timestamp: String(raw.timestamp || new Date().toISOString()),
    };
  }

  public async getPositions(): Promise<PositionsResponse> {
    const raw = await this.get(API_ENDPOINTS.POSITIONS, mockPositionsData);
    const items = Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.positions) ? raw.positions : []);
    const maxPos = Number(raw.max_positions ?? 2);
    const activeCount = Number(raw.active_count ?? items.length);

    return {
      positions: items.map((p) => normalizePosition(p as Record<string, unknown>)),
      total_open: activeCount,
      max_positions: maxPos,
      available_capacity: Math.max(0, maxPos - activeCount),
    };
  }

  public async getPnl(): Promise<PnlResponse> {
    const raw = await this.get(API_ENDPOINTS.PNL, mockPnlData);
    if (raw.summary) {
      return raw as unknown as PnlResponse;
    }

    const todayRealizedVal = Number(raw.today_realized_pnl ?? raw.today_pnl ?? raw.total_realized_pnl ?? raw.realized_pnl ?? 0);
    const totalPnlVal = Number(raw.total_pnl ?? raw.total_realized_pnl ?? todayRealizedVal);
    const cbHalted = Boolean(raw.circuit_breaker_halted ?? false);

    return {
      summary: {
        today_pnl: todayRealizedVal,
        realized_pnl: todayRealizedVal,
        unrealized_pnl: raw.unrealized_pnl !== undefined && raw.unrealized_pnl !== null ? Number(raw.unrealized_pnl) : null,
        total_pnl: totalPnlVal,
        weekly_pnl: Number(raw.weekly_pnl ?? 0),
        win_rate: Number(raw.win_rate ?? 0),
        total_trades: Number(raw.trade_count ?? raw.total_trades ?? 0),
        winning_trades: Number(raw.winning_trades ?? 0),
        losing_trades: Number(raw.losing_trades ?? 0),
        circuit_breaker_halted: cbHalted,
      },
      circuit_breaker_halted: cbHalted,
      daily_history: Array.isArray(raw.daily_history) ? (raw.daily_history as PnlResponse['daily_history']) : [],
    };
  }

  public async getToday(): Promise<TodayResponse> {
    const raw = await this.get(API_ENDPOINTS.TODAY, mockTodayData);
    const rawTrades = Array.isArray(raw.trades) ? raw.trades : [];
    const todayRealized = Number(raw.total_realized_pnl ?? raw.today_realized_pnl ?? raw.today_pnl ?? raw.realized_pnl ?? 0);

    return {
      today_pnl: todayRealized,
      realized_pnl: todayRealized,
      unrealized_pnl: raw.unrealized_pnl !== undefined && raw.unrealized_pnl !== null ? Number(raw.unrealized_pnl) : null,
      trades_count: Number(raw.total_trades ?? raw.trades_count ?? rawTrades.length),
      winning_trades: Number(raw.winning_trades ?? 0),
      losing_trades: Number(raw.losing_trades ?? 0),
      win_rate: Number(raw.win_rate ?? 0),
      trades: rawTrades.map((t) => normalizeTrade(t as Record<string, unknown>)),
    };
  }

  public async getWeek(): Promise<WeekResponse> {
    const raw = await this.get(API_ENDPOINTS.WEEK, mockWeekData);
    let rawDays: Array<Record<string, unknown>> = [];
    if (Array.isArray(raw.days)) {
      rawDays = raw.days;
    } else if (raw.daily_breakdown && typeof raw.daily_breakdown === 'object') {
      rawDays = Object.entries(raw.daily_breakdown).map(([day, pnl]) => ({
        date: day,
        pnl: Number(pnl || 0),
        trades_count: 0,
      }));
    }
    return {
      weekly_pnl: Number(raw.weekly_pnl ?? 0),
      trades_count: Number(raw.total_trades ?? raw.trades_count ?? 0),
      win_rate: Number(raw.win_rate ?? 0),
      days: rawDays.map((d: Record<string, unknown>) => ({
        date: String(d.date || ''),
        pnl: Number(d.pnl ?? d.realized_pnl ?? 0),
        trades_count: Number(d.trade_count ?? d.trades_count ?? 0),
      })),
    };
  }

  public async getTrades(): Promise<TradesResponse> {
    const raw = await this.get(API_ENDPOINTS.TRADES, mockTradesData);
    const items = Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.trades) ? raw.trades : []);
    return {
      trades: items.map((t) => normalizeTrade(t as Record<string, unknown>)),
      total_count: Number(raw.count ?? items.length),
    };
  }

  public async getBuyHistory(): Promise<BuyHistoryResponse> {
    const raw = await this.get(API_ENDPOINTS.BUY, mockBuyHistoryData);
    const items = Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.buy_orders) ? raw.buy_orders : []);
    return {
      buy_orders: items.map((t) => normalizeTrade(t as Record<string, unknown>)),
      total_count: Number(raw.count ?? items.length),
    };
  }

  public async getSellHistory(): Promise<SellHistoryResponse> {
    const raw = await this.get(API_ENDPOINTS.SELL, mockSellHistoryData);
    const items = Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.sell_orders) ? raw.sell_orders : []);
    return {
      sell_orders: items.map((t) => normalizeTrade(t as Record<string, unknown>)),
      total_count: Number(raw.count ?? items.length),
    };
  }

  public async getRisk(): Promise<RiskResponse> {
    const raw = await this.get(API_ENDPOINTS.RISK, mockRiskData);
    const riskSource = (raw.risk || raw) as Record<string, unknown>;
    const cbHalted = Boolean(riskSource.circuit_breaker_halted ?? false);
    return {
      risk: {
        trading_allowed: Boolean(riskSource.trading_allowed ?? true),
        permission_reason: riskSource.permission_reason ? String(riskSource.permission_reason) : null,
        daily_loss: Number(riskSource.daily_loss_used ?? riskSource.daily_loss ?? 0),
        daily_loss_limit: Number(riskSource.max_daily_loss ?? riskSource.daily_loss_limit ?? 2000),
        trades_today: Number(riskSource.daily_entries_used ?? riskSource.trades_today ?? 0),
        max_trades_per_day: Number(riskSource.max_daily_entries ?? riskSource.max_trades_per_day ?? 10),
        consecutive_losses: Number(riskSource.consecutive_losses ?? 0),
        max_consecutive_losses: Number(riskSource.max_consecutive_losses ?? 3),
        kill_switch_active: Boolean(riskSource.kill_switch_active ?? false),
        circuit_breaker_triggered: Boolean(cbHalted || riskSource.circuit_breaker_triggered || riskSource.kill_switch_active || false),
        circuit_breaker_halted: cbHalted,
      },
      timestamp: String(raw.timestamp || new Date().toISOString()),
    };
  }

  public async getHealth(): Promise<HealthResponse> {
    const raw = await this.get(API_ENDPOINTS.HEALTH, mockHealthData);
    const healthSource = (raw.health || raw) as Record<string, unknown>;
    const subsystems = (healthSource.subsystems || {}) as Record<string, unknown>;

    return {
      health: {
        status: String(healthSource.state || healthSource.status || 'HEALTHY'),
        uptime_seconds: healthSource.uptime_seconds !== undefined ? Number(healthSource.uptime_seconds) : undefined,
        database_status: 'SQLite WAL (Active)',
        last_successful_order_check: subsystems.last_successful_order_check ? String(subsystems.last_successful_order_check) : null,
        last_reconciliation: subsystems.last_reconciliation ? String(subsystems.last_reconciliation) : null,
        last_cycle: subsystems.last_cycle ? String(subsystems.last_cycle) : (healthSource.last_cycle ? String(healthSource.last_cycle) : null),
        last_signal: subsystems.last_received_signal ? String(subsystems.last_received_signal) : (healthSource.last_signal ? String(healthSource.last_signal) : null),
      },
      timestamp: String(raw.timestamp || new Date().toISOString()),
    };
  }

  public async getSignals(): Promise<SignalsResponse> {
    const raw = await this.get(API_ENDPOINTS.SIGNALS, mockSignalsData);
    const items = Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.signals) ? raw.signals : []);
    return {
      signals: items.map((s) => normalizeSignal(s as Record<string, unknown>)),
      total_count: Number(raw.count ?? items.length),
    };
  }

  public async getOrders(): Promise<OrdersResponse> {
    const raw = await this.get(API_ENDPOINTS.ORDERS, mockOrdersData);
    const items = Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.orders) ? raw.orders : []);
    return {
      orders: items.map((o) => normalizeOrder(o as Record<string, unknown>)),
      total_count: Number(raw.count ?? items.length),
    };
  }

  // --- Auth Endpoints ---

  public async getAuthMe(): Promise<AuthMeResponse> {
    return this.get<AuthMeResponse>(API_ENDPOINTS.AUTH_ME, mockAuthMeData) as unknown as AuthMeResponse;
  }

  public async login(req: LoginRequest): Promise<LoginResponse> {
    const mockRes: LoginResponse = {
      success: true,
      username: req.username || 'admin',
      expires_at: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
    };
    return this.post<LoginResponse>(API_ENDPOINTS.AUTH_LOGIN, req, mockRes);
  }

  public async logout(): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(
      API_ENDPOINTS.AUTH_LOGOUT,
      {},
      { success: true, message: 'Logged out successfully' }
    );
  }

  public async changePassword(req: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const mockRes: ChangePasswordResponse = {
      success: true,
      message: 'Password updated successfully. Please log in again.',
    };
    return this.post<ChangePasswordResponse>(API_ENDPOINTS.AUTH_CHANGE_PASSWORD, req, mockRes);
  }

  // --- Settings Endpoints ---

  public async getSettings(): Promise<SettingsResponse> {
    return this.get<SettingsResponse>(API_ENDPOINTS.SETTINGS, mockSettingsData) as unknown as SettingsResponse;
  }

  public async patchSettings(req: SettingsPatchRequest): Promise<SettingsPatchResponse> {
    const mockRes: SettingsPatchResponse = {
      success: true,
      updated_at: new Date().toISOString(),
      updated_keys: Object.keys(req.settings || req || {}),
      batch_id: 'mock-batch-id',
      settings: mockSettingsData.settings,
    };
    return this.patch<SettingsPatchResponse>(API_ENDPOINTS.SETTINGS, req, mockRes);
  }

  // --- Circuit Breaker Control Endpoints ---

  public async resetCircuitBreaker(req: ResetCircuitBreakerRequest): Promise<ResetCircuitBreakerResponse> {
    return this.post<ResetCircuitBreakerResponse>(
      API_ENDPOINTS.RISK_RESET_CIRCUIT_BREAKER,
      req,
      mockResetCircuitBreakerData
    );
  }
}

export const apiClient = new ApiClient();
